import { ArrowRight, Star, ShieldCheck, Zap, BookOpen, Dumbbell, ClipboardList, CheckSquare, Download } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { usePricing, fmtPrice } from "@/hooks/usePricing";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const includes = [
  { icon: BookOpen, label: "Guida completa", sub: "31 pagine" },
  { icon: Dumbbell, label: "Esercizi guidati", sub: "pronti all'uso" },
  { icon: ClipboardList, label: "Esempi per materia", sub: "concreti" },
  { icon: CheckSquare, label: "Metodo in 4 fasi", sub: "applicabile subito" },
];

export default function Hero() {
  const pricing = usePricing();
  const price = fmtPrice(pricing.main.price);
  const compare = pricing.main.compare_at ? fmtPrice(pricing.main.compare_at) : null;
  return (
    <section id="hero" data-testid={HERO.section} className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      <div className="absolute inset-0 ps-grid-bg opacity-70" />
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#f0d203]/20 blur-3xl" />
      <div className="absolute top-40 -left-32 w-[380px] h-[380px] rounded-full bg-[#5982c5]/20 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* LEFT — copy */}
        <div className="ps-reveal">
          <span className="inline-flex items-center gap-2 bg-white/70 border border-[#5982c5]/20 text-[#5982c5] text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-full">
            <Zap size={14} className="text-[#f0d203] fill-[#f0d203]" />
            Manuale digitale · Download immediato
          </span>

          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#1f2937]">
            Leggi più in fretta, ricorda ciò che studi e{" "}
            <span className="relative inline-block text-[#5982c5]">
              alza i tuoi voti
              <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none">
                <path d="M2 9C50 3 150 3 198 9" stroke="#f0d203" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </span>.
          </h1>

          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-[#4b5563] max-w-xl">
            <strong className="text-[#1f2937]">ComprehensionUp</strong> è il manuale pratico che ti guida
            dalla prima pagina all'esame con un processo in 4 fasi —
            <span className="text-[#5982c5] font-semibold"> Attenzione, Elaborazione, Collegamento ed Esposizione</span>.
            Dentro trovi spiegazioni, esempi per materia ed esercizi pronti da usare sui tuoi testi.
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {includes.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="bg-white/70 backdrop-blur rounded-2xl px-3 py-3 border border-[#5982c5]/10 flex flex-col items-start gap-1.5">
                  <Icon size={20} className="text-[#5982c5]" />
                  <div>
                    <p className="text-sm font-bold text-[#1f2937] leading-tight">{it.label}</p>
                    <p className="text-xs text-[#9ca3af]">{it.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              data-testid={HERO.ctaPrimary}
              onClick={() => scrollTo("checkout")}
              className="w-full sm:w-auto bg-[#f0d203] text-[#1a1a1a] hover:bg-[#dcb902] hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300 font-bold px-8 py-4 rounded-full flex items-center justify-center gap-2"
            >
              Ottieni ComprehensionUp a {price}{" "}
              {compare && <span className="font-semibold text-[#1a1a1a]/55 line-through">{compare}</span>}
              <ArrowRight size={20} />
            </button>
            <button
              data-testid={HERO.ctaSecondary}
              onClick={() => scrollTo("manuale")}
              className="w-full sm:w-auto bg-transparent border-2 border-[#5982c5] text-[#5982c5] hover:bg-[#5982c5] hover:text-white transition-all duration-300 font-semibold px-8 py-4 rounded-full"
            >
              Guarda cosa contiene
            </button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="text-[#f0d203] fill-[#f0d203]" />
                ))}
              </div>
              <span className="text-sm font-semibold text-[#4b5563]">Oltre 1.000 studenti aiutati</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#4b5563]">
              <ShieldCheck size={18} className="text-[#5982c5]" /> Accesso a vita
            </div>
          </div>
        </div>

        {/* RIGHT — single hero image */}
        <div className="ps-reveal" style={{ animationDelay: "0.15s" }}>
          <div className="relative">
            <div className="absolute -inset-3 bg-[#5982c5]/10 rounded-[2.5rem] blur-sm" />
            <img
              src="/hero-main.png"
              alt="ComprehensionUp: dalla prima pagina all'esame, su computer, tablet e smartphone"
              data-testid="hero-main-image"
              className="relative w-full rounded-[2rem] shadow-[0_30px_60px_rgb(89,130,197,0.25)]"
            />
            <div className="absolute -bottom-4 -left-3 bg-[#f0d203] text-[#1a1a1a] rounded-2xl px-4 py-2.5 shadow-lg flex items-center gap-2">
              <Download size={16} />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide leading-none">Download immediato</p>
                <p className="font-heading font-extrabold text-base leading-tight">
                  PDF · solo {price} {compare && <span className="text-sm font-semibold text-[#1a1a1a]/55 line-through">{compare}</span>}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
